import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ArrayHelpers, FieldArray, getIn, useFormikContext } from 'formik';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Col, ColProps, Form, Row } from 'react-bootstrap';
import { JSX } from 'react/jsx-runtime';

type ChildrenType = {
  controlId: string;
  index: number;
  disabled: boolean;
  remove: ArrayHelpers['remove'];
  current: any;
};

export type ListHeaderType = { size?: ColProps['xs']; label: string; className?: string };

export type ListProps = {
  children: (props: ChildrenType) => JSX.Element;
  controlId: string;
  model: object | string | number;
  initialValidation?: object | boolean;
  label?: string;
  headers?: ListHeaderType[];
  className?: string;
  placeholder?: string;
  allowElement?: (element: object | string | number) => boolean;
  addIcon?: IconName;
  disabled?: boolean;
  showIndex?: boolean;
};

export const List = ({
  controlId,
  model,
  initialValidation,
  label,
  headers,
  className,
  placeholder,
  allowElement = () => true,
  addIcon = 'plus-circle',
  disabled = false,
  showIndex = true,
  children,
}: ListProps) => {
  const { values, setTouched, touched } = useFormikContext();

  const elements = useMemo(() => getIn(values, controlId) as any[], [values, controlId]);

  const computedElementsIds = useMemo(
    () =>
      elements.reduce((acc: any[][], current: any, index) => {
        if (allowElement(current)) {
          acc.push([index, current]);
        }

        return acc;
      }, []),
    [allowElement, elements]
  );

  const computedHeaders = useMemo(
    () => (
      <Row>
        {showIndex && <Col xs={1} />}
        {headers
          ? headers.map((header, index) => (
              <Col
                xs={header.size === 'auto' || !header.size ? undefined : header.size}
                key={index}
                className={`${header.className} d-flex`}
              >
                <span>{header.label}</span>
              </Col>
            ))
          : undefined}
      </Row>
    ),
    [headers, showIndex]
  );

  const addElement = (callback: any) => {
    callback(model);

    if (initialValidation) {
      const newTouched = JSON.parse(JSON.stringify(touched));

      setTouched(_.set(newTouched, `${controlId}.${elements.length}`, initialValidation));
    }
  };

  return (
    <div className={`list${className ? ` ${className}` : ''}`}>
      <FieldArray name={controlId}>
        {({ remove, push }: ArrayHelpers) => {
          if (!Array.isArray(elements)) {
            return <div style={{ color: 'red' }}>Element pointed by the controlId of a list should be an array!</div>;
          }

          return (
            <>
              {label && (
                <Row>
                  <Col>
                    <Form.Label>{label}</Form.Label>
                  </Col>
                </Row>
              )}
              {headers && computedElementsIds.length > 0 && <Row className="list-row">{computedHeaders}</Row>}
              {computedElementsIds.map((index) => (
                <Row key={index[0]} className="list-row">
                  {showIndex && (
                    <Col xs={1} className="d-flex align-items-center justify-content-center">
                      <span>{index[0] + 1}</span>
                    </Col>
                  )}
                  {children({
                    controlId: `${controlId}.${index[0]}`,
                    index: index[0],
                    disabled,
                    remove,
                    current: index[1],
                  })}
                </Row>
              ))}
              {computedElementsIds.length === 0 && (
                <Row>
                  <Col xs={11} className="text-center">
                    <p>{placeholder}</p>
                  </Col>
                </Row>
              )}
              {!disabled && (
                <Row className="list-row mt-3">
                  <Col xs={1}>
                    <FontAwesomeIcon className="add-icon" icon={addIcon} onClick={() => addElement(push)} />
                  </Col>
                </Row>
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};
